import * as z from "zod";
import { Id } from "./ids.js";

export const TICKET_STATUSES = ["todo", "doing", "review", "blocked", "done", "closed"] as const;
export const TicketStatusSchema = z.enum(TICKET_STATUSES);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

/** `done` is finished-but-open for review; `closed` is terminal, so both stamp completion. */
export function isTicketCompletedStatus(status: TicketStatus): boolean {
  return status === "done" || status === "closed";
}

export const TICKET_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export const TicketPrioritySchema = z.enum(TICKET_PRIORITIES);
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;
const DEFAULT_TICKET_PRIORITY: TicketPriority = "normal";

export const TICKET_TITLE_MAX_LENGTH = 200;
export const TICKET_DESCRIPTION_MAX_LENGTH = 20_000;
const TICKET_PREFIX_MAX_LENGTH = 12;
// Ticket numbers are stored as PostgreSQL Int values.
const TICKET_NUMBER_MAX = 2_147_483_647;
export const TICKET_COMMENT_MAX_LENGTH = 10_000;

const TicketPrefixSchema = z
  .string()
  .trim()
  .min(1)
  .max(TICKET_PREFIX_MAX_LENGTH)
  .regex(
    /^[A-Za-z][A-Za-z0-9]*$/,
    "Prefix must start with a letter and contain only letters and digits",
  );

export function ticketRef(prefix: string, number: number): string {
  return `${prefix}-${number}`;
}

export function parseTicketRef(
  value: string,
  expectedPrefix?: string,
): { prefix: string; number: number } | null {
  const match = /^([A-Za-z][A-Za-z0-9]*)-([1-9][0-9]*)$/.exec(value.trim());
  if (!match) return null;
  const [, rawPrefix, rawNumber] = match;
  if (!rawPrefix || !rawNumber) return null;
  const prefix = rawPrefix.toUpperCase();
  if (expectedPrefix !== undefined && prefix !== expectedPrefix.trim().toUpperCase()) return null;
  const number = Number(rawNumber);
  if (number > TICKET_NUMBER_MAX) return null;
  return { prefix, number };
}

export const BoardSchema = z.object({
  id: Id,
  spaceId: Id,
  name: z.string(),
  ticketPrefix: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Board = z.infer<typeof BoardSchema>;

export const RenameBoardInput = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    ticketPrefix: TicketPrefixSchema.optional(),
  })
  .refine((value) => value.name !== undefined || value.ticketPrefix !== undefined, {
    message: "Provide a name or ticketPrefix",
  });
export type RenameBoardInput = z.infer<typeof RenameBoardInput>;

export const TicketCommentSchema = z.object({
  id: Id,
  ticketId: Id,
  body: z.string(),
  authorBotId: Id.nullable(),
  authorUserId: Id.nullable(),
  authorName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TicketComment = z.infer<typeof TicketCommentSchema>;

export const TicketSchema = z.object({
  id: Id,
  boardId: Id,
  spaceId: Id,
  number: z.number().int().positive(),
  ref: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  acceptanceCriteria: z.array(z.string()),
  status: TicketStatusSchema,
  priority: TicketPrioritySchema,
  assigneeBotId: Id.nullable(),
  createdByBotId: Id.nullable(),
  createdByUserId: Id.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type Ticket = z.infer<typeof TicketSchema>;

export const TICKET_SUBMISSION_ARTIFACT_LIMIT = 20;
export const TicketSubmissionStatusSchema = z.enum(["pending", "accepted", "returned"]);
export type TicketSubmissionStatus = z.infer<typeof TicketSubmissionStatusSchema>;

export const TicketSubmissionArtifactSchema = z.object({
  artifactId: Id,
  version: z.number().int().positive(),
  name: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type TicketSubmissionArtifact = z.infer<typeof TicketSubmissionArtifactSchema>;

export const TicketSubmissionSchema = z.object({
  id: Id,
  ticketId: Id,
  round: z.number().int().positive(),
  runId: Id,
  submittedByBotId: Id,
  submittedByBotName: z.string(),
  summary: z.string(),
  acceptanceCriteria: z.array(z.string()),
  status: TicketSubmissionStatusSchema,
  decidedByUserId: Id.nullable(),
  decidedAt: z.string().nullable(),
  decisionReason: z.string().nullable(),
  reworkRunId: Id.nullable(),
  createdAt: z.string(),
  artifacts: z.array(TicketSubmissionArtifactSchema),
});
export type TicketSubmission = z.infer<typeof TicketSubmissionSchema>;

export const ListTicketSubmissionsInput = z.object({ ticketId: Id });
export type ListTicketSubmissionsInput = z.infer<typeof ListTicketSubmissionsInput>;
export const ListTicketSubmissionsOutput = z.object({
  ticket: TicketSchema,
  submissions: z.array(TicketSubmissionSchema),
});
export type ListTicketSubmissionsOutput = z.infer<typeof ListTicketSubmissionsOutput>;

export const SubmitTicketInput = z
  .object({
    ticketId: Id,
    summary: z.string().trim().min(1).max(10_000),
    artifactIds: z.array(Id).min(1).max(TICKET_SUBMISSION_ARTIFACT_LIMIT),
  })
  .superRefine((input, ctx) => {
    if (new Set(input.artifactIds).size !== input.artifactIds.length) {
      ctx.addIssue({
        code: "custom",
        message: "Artifact ids must be unique",
        path: ["artifactIds"],
      });
    }
  });
export type SubmitTicketInput = z.infer<typeof SubmitTicketInput>;

export type DecideTicketSubmissionInput = z.infer<typeof DecideTicketSubmissionInput>;
export type DecideTicketSubmissionOutput = z.infer<typeof DecideTicketSubmissionOutput>;

export const DecideTicketSubmissionInput = z
  .object({
    ticketId: Id,
    submissionId: Id,
    decision: z.enum(["accept", "return"]),
    reason: z.string().trim().max(2_000).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.decision === "return" && !input.reason) {
      ctx.addIssue({
        code: "custom",
        message: "A reason is required when returning work",
        path: ["reason"],
      });
    }
  });

export const DecideTicketSubmissionOutput = z.object({
  ticket: TicketSchema,
  submission: TicketSubmissionSchema,
});

export const ListTicketsInput = z.object({
  boardId: Id.optional(),
  status: TicketStatusSchema.optional(),
  assigneeBotId: Id.optional(),
  q: z.string().trim().max(200).optional(),
});
export type ListTicketsInput = z.infer<typeof ListTicketsInput>;

export const ListTicketsOutput = z.object({ tickets: z.array(TicketSchema) });
export type ListTicketsOutput = z.infer<typeof ListTicketsOutput>;

export const GetTicketInput = z
  .object({
    id: Id.optional(),
    ref: z.string().trim().min(1).max(120).optional(),
  })
  .refine((value) => Boolean(value.id) !== Boolean(value.ref), {
    message: "Provide exactly one of id or ref",
  });
export type GetTicketInput = z.infer<typeof GetTicketInput>;

export const CreateTicketInput = z.object({
  boardId: Id.optional(),
  title: z.string().trim().min(1).max(TICKET_TITLE_MAX_LENGTH),
  description: z.string().max(TICKET_DESCRIPTION_MAX_LENGTH).optional(),
  priority: TicketPrioritySchema.default(DEFAULT_TICKET_PRIORITY),
  assigneeBotId: Id,
});
export type CreateTicketInput = z.infer<typeof CreateTicketInput>;

export const UpdateTicketInput = z.object({
  id: Id,
  title: z.string().trim().min(1).max(TICKET_TITLE_MAX_LENGTH).optional(),
  description: z.string().max(TICKET_DESCRIPTION_MAX_LENGTH).nullable().optional(),
  priority: TicketPrioritySchema.nullable().optional(),
  status: TicketStatusSchema.optional(),
  assigneeBotId: Id.optional(),
});
export type UpdateTicketInput = z.infer<typeof UpdateTicketInput>;

export const MoveTicketInput = z.object({
  id: Id,
  status: TicketStatusSchema,
});
export type MoveTicketInput = z.infer<typeof MoveTicketInput>;

export const CommentTicketInput = z.object({
  ticketId: Id,
  body: z.string().trim().min(1).max(TICKET_COMMENT_MAX_LENGTH),
});
export type CommentTicketInput = z.infer<typeof CommentTicketInput>;

export const BoardEventSchema = z.object({
  spaceId: Id,
  boardId: Id.optional(),
  ticketId: Id.optional(),
  createdAt: z.string(),
});
export type BoardEvent = z.infer<typeof BoardEventSchema>;
